import React, { FC, useEffect, useState } from "react";
import {
    Button,
    Col,
    Collapse,
    FormControl,
    InputGroup,
    Modal,
    Row
} from "react-bootstrap";
import {
    AnalyticsEventSchema,
    AnalyticsSchema,
    MeData
} from "../../../models/types";
import { AnalyticsEventLibraryItem } from "../../variationItems/analytics/AnalyticsEventLibraryItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { ADMIN_DELETE_ANALYTICS_LIB_EVENT } from "../../../graphql/mutations";
import { GET_ANALYTICS_EVENTS, GET_ME } from "../../../graphql/queries";

interface Props {
    show: boolean;
    analyticsEvents: AnalyticsEventSchema[] | undefined;
    updateSchema: (schema: AnalyticsEventSchema) => void;
    currentSchema: AnalyticsSchema | undefined;
    onClose: () => void;
}

export const AnalyticsEventLibraryModal: FC<Props> = ({
    show,
    analyticsEvents,
    updateSchema,
    currentSchema,
    onClose
}) => {
    const [showInfo, setShowInfo] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const client = useApolloClient();

    useEffect(() => {
        if (!show) {
            setSearchTerm("");
            setShowInfo(false);
        }
    }, [show]);

    const [removeAnalyticsLibEventMutation] = useMutation<
        { adminDeleteAnalyticsLibEvent: { success: boolean } },
        { eventId: number }
    >(ADMIN_DELETE_ANALYTICS_LIB_EVENT);

    const { data: { me } = {} } = useQuery<MeData>(GET_ME, {
        fetchPolicy: "cache-first",
        skip: !show
    });

    const { data: { events } = {} } = useQuery<AnalyticsSchema>(
        GET_ANALYTICS_EVENTS,
        {
            fetchPolicy: "cache-first",
            skip: !show
        }
    );

    const removeLibEvent = async (eventId: number) => {
        try {
            await removeAnalyticsLibEventMutation({
                variables: {
                    eventId: eventId
                },
                update: cache => {
                    try {
                        const currentEventsData =
                            cache.readQuery<AnalyticsSchema>({
                                query: GET_ANALYTICS_EVENTS
                            });

                        if (!currentEventsData?.events) {
                            client.query({
                                query: GET_ANALYTICS_EVENTS,
                                fetchPolicy: "network-only"
                            });
                            return;
                        }

                        const newEvents = currentEventsData.events.filter(
                            element => element.id !== eventId
                        );

                        cache.writeQuery<AnalyticsSchema>({
                            query: GET_ANALYTICS_EVENTS,
                            data: { events: newEvents }
                        });
                    } catch (error) {
                        console.log("[DEBUG] Cache update error:", error);
                    }
                }
            });
        } catch (error) {
            console.log("[DEBUG] removeLibEvent error: ", error);
        }
    };

    const filteredEvents = (events || analyticsEvents)?.filter(
        event =>
            !event.frozen &&
            event.eventName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const frozenEvents = (events || analyticsEvents)?.filter(
        event => event.frozen
    );

    const frozenList = frozenEvents?.map(
        (event: AnalyticsEventSchema, index: number) => (
            <Col xl={6} key={event.eventName + index}>
                <AnalyticsEventLibraryItem
                    event={event}
                    updateAnalyticsSchema={updateSchema}
                    removeLibEvent={removeLibEvent}
                    user={me}
                    currentSchema={currentSchema}
                />
            </Col>
        )
    );

    const eventsList = filteredEvents?.map(
        (event: AnalyticsEventSchema, index: number) => (
            <Col xl={6} key={event.eventName + index}>
                <AnalyticsEventLibraryItem
                    event={event}
                    updateAnalyticsSchema={updateSchema}
                    removeLibEvent={removeLibEvent}
                    user={me}
                    currentSchema={currentSchema}
                />
            </Col>
        )
    );

    return (
        <Modal show={show} onHide={onClose} size="xl" keyboard={true}>
            <Modal.Header>
                <Modal.Title>Event Library</Modal.Title>
                <FontAwesomeIcon
                    icon={faQuestionCircle}
                    title="Toggle info"
                    className="text-secondary-75 mouseHover ms-2"
                    onClick={() => setShowInfo(!showInfo)}
                />
                <div className="text-right ms-auto me-2">
                    <InputGroup>
                        <InputGroup.Text>Search</InputGroup.Text>
                        <FormControl
                            placeholder="Search event..."
                            value={searchTerm}
                            onChange={event =>
                                setSearchTerm(event.target.value)
                            }
                        />
                    </InputGroup>
                </div>
            </Modal.Header>
            <Modal.Body className="px-2">
                <Collapse in={showInfo}>
                    <div>
                        <p className="px-3 bread-text">
                            Add events from this list to current schema. <br />{" "}
                            Open the event to see event data and comments.{" "}
                            <br /> Added events can be edited in the Schema view
                            if they are not mandatory frozen items. <br />
                            Frozen library items can be edited in the Admin
                            panel.
                        </p>
                    </div>
                </Collapse>
                {frozenEvents && frozenEvents.length > 0 && (
                    <Row className="mb-2">{frozenList}</Row>
                )}
                {filteredEvents && filteredEvents.length > 0 ? (
                    <Row>{eventsList}</Row>
                ) : (
                    <p className="bread-text px-3">No events found.</p>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-center pt-1">
                <Button variant="secondary w-25" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
